import toast from "react-hot-toast";
import { useState, useEffect } from 'react';
import api from '../Services/api';
import '../styles/dashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBell, faSearch, faCalendarAlt, faChevronRight, 
    faUserTie, faClipboardCheck, faTasks, faUsers, 
    faIndustry, faHospital, faCalendarDays, faCheckCircle,
    faEllipsisH, faMapMarkerAlt, faEnvelope, faMessage,
    faUserInjured, faUserGraduate, faShoppingCart, faTools, faKey, faChartLine,
    faBox, faClipboardList, faTruck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Contexts/AuthContext';
import { useNotifications } from '../Contexts/NotificationContext';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const iconMap = {
    faUserTie, faClipboardCheck, faTasks, faUsers, 
    faIndustry, faHospital, faCalendarDays, faUserInjured, 
    faUserGraduate, faShoppingCart, faTools, faKey, faChartLine,
    faBox, faClipboardList, faTruck
};

// --- PREMIUM COMPONENTS ---

const StatCard = ({ label, value, change, up, color, iconColor, icon }) => (
    <div className="p-stat-card">
        <div className="p-stat-main">
            <div className="p-stat-info">
                <span className="p-stat-label">{label}</span>
                <h3 className="p-stat-value">{value.toLocaleString()}</h3>
                <div className={`p-stat-trend ${up ? 'up' : 'down'}`}>
                    {up ? '↑' : '↓'} {change} <span>since last month</span>
                </div>
            </div>
            <div className="p-stat-icon-wrap" style={{ backgroundColor: color }}>
                <FontAwesomeIcon icon={iconMap[icon] || faTasks} style={{ color: iconColor }} />
            </div>
        </div>
    </div>
);

const ActivityItem = ({ name, action, time, dot }) => (
    <div className="p-activity-item">
        <div className="p-activity-dot" style={{ backgroundColor: dot }}></div>
        <div className="p-activity-content">
            <div className="p-activity-text"><strong>{name}</strong> {action}</div>
            <div className="p-activity-time">{time}</div>
        </div>
        <FontAwesomeIcon icon={faChevronRight} className="p-activity-arrow" />
    </div>
);

const TaskItem = ({ label, done }) => (
    <div className="p-task-item">
        <div className={`p-task-check ${done ? 'done' : ''}`}>
            {done && <FontAwesomeIcon icon={faCheckCircle} />}
        </div>
        <span className="p-task-label">{label}</span>
        <FontAwesomeIcon icon={faEllipsisH} className="p-task-more" />
    </div>
);

const ProfileCard = ({ user, stats, onViewProfile }) => (
    <div className="p-profile-card">
        <div className="p-profile-banner"></div>
        <div className="p-profile-content">
            <div className="p-profile-avatar" onClick={onViewProfile} style={{ cursor: 'pointer' }}>{user.avatar}</div>
            <h3 className="p-profile-name" onClick={onViewProfile} style={{ cursor: 'pointer' }}>{user.name}</h3>
            <p className="p-profile-role">{user.role}</p>
            
            <div className="p-profile-meta">
                <span><FontAwesomeIcon icon={faMapMarkerAlt} /> {user.location}</span>
                <span><FontAwesomeIcon icon={faEnvelope} /> {user.email}</span>
            </div>

            <div className="p-profile-stats">
                <div className="p-pstat">
                    <strong>{stats?.find(s => s.label.includes('Leads') || s.label.includes('Members'))?.value || 0}</strong>
                    <span>{stats?.find(s => s.label.includes('Leads')) ? 'Leads' : 'Members'}</span>
                </div>
                <div className="p-pstat">
                    <strong>{stats?.find(s => s.label.includes('Conversion') || s.label.includes('Roles'))?.value || 'N/A'}</strong>
                    <span>{stats?.find(s => s.label.includes('Conversion')) ? 'Conversion' : 'Roles'}</span>
                </div>
                <div className="p-pstat"><strong>{user.joined?.split(' ')[1] || '2026'}</strong><span>Joined</span></div>
            </div>

            <button className="p-profile-btn" onClick={(e) => { e.preventDefault(); onViewProfile(); }}>View Full Profile</button>
        </div>
    </div>
);

const ProfileDetailModal = ({ user, onClose }) => (
    <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content profile-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0 }}>User Profile</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', fontWeight: 800, flexShrink: 0 }}>
                    {user.avatar}
                </div>
                <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px 0' }}>{user.name}</h3>
                    <p style={{ color: '#3b82f6', fontWeight: 700, margin: '0 0 16px 0', fontSize: '14px', textTransform: 'uppercase' }}>{user.role}</p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div className="p-info-group">
                            <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Email Address</label>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{user.email}</span>
                        </div>
                        <div className="p-info-group">
                            <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Location</label>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{user.location}</span>
                        </div>
                        <div className="p-info-group">
                            <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Member Since</label>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>{user.joined}</span>
                        </div>
                        <div className="p-info-group">
                            <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Employment Type</label>
                            <span style={{ fontSize: '14px', fontWeight: 600 }}>Permanent</span>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '12px' }}>
                <button className="btn-primary" onClick={() => window.location.href='/profile'} style={{ flex: 1 }}>Manage Account</button>
                <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
        </div>
    </div>
);

// --- MANAGEMENT UTILS ---
const DASHBOARD_ENDPOINTS = {
    industry: 'industries', 
    industries: 'industries',
    org: 'organizations',
    organizations: 'organizations',
    user: 'users',
    users: 'users'
};

const AdminManagement = ({ data, refresh }) => {
    const [activeTab, setActiveTab] = useState('users');
    const [showModal, setShowModal] = useState(null); // 'user'
    const [form, setForm] = useState({});
    const [editId, setEditId] = useState(null);

    const closeModal = () => { setShowModal(null); setForm({}); setEditId(null); };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const endpoint = DASHBOARD_ENDPOINTS[showModal];

            if (editId) {
                await api.put(`dashboard/${endpoint}/${editId}/`, form);
            } else {
                await api.post(`dashboard/${endpoint}/`, form);
            }
            closeModal();
            refresh();
        } catch (err) { 
            alert("Action failed. Check console."); 
            console.error(err.response?.data);
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            await api.delete(`dashboard/${DASHBOARD_ENDPOINTS[type]}/${id}/`);
            refresh();
        } catch (err) { alert("Delete failed."); }
    };

    return (
        <div className="p-admin-section">
            <div className="p-section-header">
                <h3>User & Access Management</h3>
                <div className="p-tabs">
                    <button className="active">Users</button>
                </div>
            </div>

            <div className="p-tab-actions" style={{padding: '1rem', display: 'flex', justifyContent: 'flex-end'}}>
                <button className="btn-primary" onClick={() => {
                   setShowModal('user');
                }}>+ New User</button>
            </div>

            <div className="p-tab-content">
                 {activeTab === 'users' && (
                     <div className="p-table-wrap">
                        <table className="p-table">
                            <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
                           {/* Replace the existing <tbody> inside AdminManagement with this */}
<tbody>
    {data.users?.map(u => (
        <tr key={u.id}>
            <td data-label="User">
                <strong>{u.username}</strong><br/><small>{u.email}</small>
            </td>
            <td data-label="Role">{u.role_name}</td>
            <td data-label="Status">
                {u.is_active !== false ? (
                    <span className="p-mini-badge success">Active</span>
                ) : (
                    <span className="p-mini-badge danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}>Deactivated</span>
                )}
            </td>
            <td data-label="Actions">
                <button className="row-btn" onClick={() => { setForm(u); setEditId(u.id); setShowModal('user'); }}>Edit</button>
                <button 
                    className="row-btn" 
                    onClick={async () => {
                        const action = u.is_active !== false ? "deactivate" : "reactivate";
                        if (window.confirm(`Are you sure you want to ${action} this user?`)) {
                            try {
                                await api.put(`accounts/users/${u.id}/`, { ...u, is_active: u.is_active === false });
                                toast.success(`User ${action}d successfully`);
                                refresh();
                            } catch (e) {
                                toast.error(`Failed to ${action} user`);
                            }
                        }
                    }}
                    style={{ marginLeft: '4px', backgroundColor: u.is_active !== false ? '#fee2e2' : '#d1fae5', color: u.is_active !== false ? '#dc2626' : '#059669', border: '1px solid transparent' }}
                >
                    {u.is_active !== false ? "Deactivate" : "Reactivate"}
                </button>
                <button className="row-btn delete" onClick={() => handleDelete('users', u.id)}>Delete</button>
            </td>
        </tr>
    ))}
</tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>{editId ? "Edit" : "Add"} {showModal.charAt(0).toUpperCase() + showModal.slice(1)}</h2>
                        <form onSubmit={handleSave}>
                            {showModal === 'industry' && (
                                <>
                                    <div className="form-group">
                                        <label>Name</label>
                                        <input required value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea value={form.description || ""} onChange={e => setForm({...form, description: e.target.value})} />
                                    </div>
                                </>
                            )}
                            {showModal === 'org' && (
                                <>
                                    <div className="form-group">
                                        <label>Organization Name</label>
                                        <input required value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Industry</label>
                                        <select required value={form.industry || ""} onChange={e => setForm({...form, industry: e.target.value})}>
                                            <option value="">Select Industry...</option>
                                            {data.industries?.map(ind => <option key={ind.id} value={ind.id}>{ind.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Address</label>
                                        <input value={form.address || ""} onChange={e => setForm({...form, address: e.target.value})} />
                                    </div>
                                </>
                            )}
                            {showModal === 'user' && (
                                <>
                                    <div className="form-group">
                                        <label>Username</label>
                                        <input required value={form.username || ""} onChange={e => setForm({...form, username: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input type="email" required value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Password {editId && "(Leave blank to keep same)"}</label>
                                        <input type="password" required={!editId} value={form.password || ""} onChange={e => setForm({...form, password: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Organization</label>
                                        <select value={form.organization || ""} onChange={e => setForm({...form, organization: e.target.value})}>
                                            <option value="">Global (Superuser)</option>
                                            {data.organizations?.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{flexDirection: 'row', gap: '1rem', alignItems: 'center'}}>
                                        <input type="checkbox" checked={form.is_superuser || false} onChange={e => setForm({...form, is_superuser: e.target.checked})} />
                                        <label style={{marginBottom: 0}}>Is Superuser?</label>
                                    </div>
                                </>
                            )}
                            <div className="modal-footer" style={{marginTop: '1.5rem'}}>
                                <button type="button" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn-save">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const PersonalProgressCard = ({ metrics }) => (
    <div className="p-card personal-card" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white', border: 'none', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)' }}>
        <div className="p-card-header">
            <h3 style={{ color: 'white' }}>My Daily Progress</h3>
            <span className="p-mini-badge" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '4px 10px' }}>Today</span>
        </div>
        <div className="personal-metrics-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
            <div className="metric-box" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>Calls Made</div>
                <div style={{ fontSize: '26px', fontWeight: '800' }}>{metrics?.calls_today || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '8px' }}>
                    Total calls tracked today
                </div>
            </div>
            <div className="metric-box" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>Connected</div>
                <div style={{ fontSize: '26px', fontWeight: '800', color: '#34d399' }}>{metrics?.connected_today || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '8px' }}>
                    {metrics?.calls_today > 0 ? ((metrics.connected_today / metrics.calls_today) * 100).toFixed(0) : 0}% Connection Rate
                </div>
            </div>
        </div>
        <div className="conversion-metrics" style={{ marginTop: '25px', display: 'flex', justifyContent: 'space-between', padding: '0 10px' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#22c55e' }}>{metrics?.interested_today || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '4px' }}>Interested</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#f59e0b' }}>{metrics?.form_filled_today || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '4px' }}>Form Filled</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#a855f7' }}>{metrics?.admission_done_today || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '4px' }}>Admission</div>
            </div>
        </div>
        <button 
            onClick={() => window.location.href='/self-analysis'} 
            style={{ width: '100%', marginTop: '25px', padding: '12px', borderRadius: '10px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'background 0.2s' }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
        >
            View Detailed Analysis
        </button>
    </div>
);

// --- MAIN DISPATCHER ---

export default function Dashboard() {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const navigate = useNavigate();
    const [showNotifications, setShowNotifications] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);

    const fetchDashboard = async () => {
        try {
            const response = await api.get("dashboard/stats/");
            setData(response.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDashboard(); }, []);

    const { onboardingComplete } = useAuth();

    if (loading) return <div className="p-loading">Initializing Neural Workspace...</div>;
    if (error) return <div className="p-error">Failed to sync: {error}</div>;

    const { user_profile, stats, activities, tasks } = data;

    return (
        <div className="p-dashboard-root">
            {/* Onboarding Overlay Disabled in Private Edition */}
            {/* 
            {!onboardingComplete && (
                <div className="onboarding-overlay">
                    <div className="onboarding-card">
                        <div className="onboarding-icon">🚀</div>
                        <h2>Complete Your Organization Setup</h2>
                        <p>Welcome! Before you can start managing your team and using platform features, you need to complete your organization profile.</p>
                        <ul className="onboarding-steps">
                            <li>Fill billing details</li>
                            <li>Configure tax identity</li>
                            <li>Verify contact information</li>
                        </ul>
                        <button className="onboarding-btn" onClick={() => navigate('/organization-profile')}>
                            Go to Setup Profile
                        </button>
                    </div>
                </div>
            )}
            */}
            {/* Header Block */}
            <header className="p-header">
                <div className="p-header-left">
                    <h1>Good morning, <span onClick={() => setShowProfileModal(true)} style={{ cursor: 'pointer', color: '#2563eb' }}>{user_profile.name}</span> 👋</h1>
                    <p>Here's what's happening in your {data.organization_name || 'Global'} workspace today.</p>
                </div>
                <div className="p-header-right">
                    <div className="p-search-box">
                        <FontAwesomeIcon icon={faSearch} />
                        <input placeholder="Search records..." />
                    </div>
                    <div className="p-icon-btn" onClick={() => navigate('/calendar')} title="Calendar">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                    <div className={`p-icon-btn ${unreadCount > 0 ? 'active' : ''}`} 
                         onClick={() => setShowNotifications(!showNotifications)}
                         title="Notifications">
                        <FontAwesomeIcon icon={faBell} />
                        {unreadCount > 0 && <span className="dot">{unreadCount}</span>}
                        
                        {showNotifications && (
                            <div className="p-notification-dropdown" onClick={(e) => e.stopPropagation()}>
                                <div className="p-nd-header">
                                    <span>Notifications</span>
                                    <button className="mark-all-btn" onClick={markAllAsRead}>Mark all read</button>
                                </div>
                                <div className="p-nd-body">
                                    {notifications.length === 0 ? (
                                        <div className="p-nd-empty">No notifications</div>
                                    ) : (
                                        notifications.map(n => (
                                            <div 
                                                key={n.id} 
                                                className={`p-nd-item ${n.is_read ? 'read' : ''}`} 
                                                onClick={() => { 
                                                    markAsRead(n.id);
                                                    if (n.link) navigate(n.link);
                                                    setShowNotifications(false); 
                                                }}
                                            >
                                                <div className="p-nd-icon">
                                                    <FontAwesomeIcon icon={n.notification_type === 'message' ? faMessage : faBell} />
                                                </div>
                                                <div className="p-nd-info">
                                                    <div className="p-nd-title">{n.title}</div>
                                                    <div className="p-nd-msg">{n.content}</div>
                                                    <div className="p-nd-time">{new Date(n.created_at).toLocaleString()}</div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="p-nd-footer">
                                    <button onClick={() => { markAllAsRead(); setShowNotifications(false); }}>Clear All</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Stats Grid */}
            <section className="p-stats-grid">
                {stats.map((s, idx) => <StatCard key={idx} {...s} />)}
            </section>

            {/* Main Content Grid */}
            <div className="p-main-grid">
                <div className="p-grid-left">
                    {/* Activity Feed */}
                    <div className="p-card">
                        <div className="p-card-header">
                            <h3>Recent Activity</h3>
                            <button>View All</button>
                        </div>
                        <div className="p-activity-list">
                            {activities.map((a, idx) => <ActivityItem key={idx} {...a} />)}
                        </div>
                    </div>

                    {/* Today's Tasks */}
                    <div className="p-card">
                        <div className="p-card-header">
                            <h3>Today's Agenda</h3>
                            <button onClick={() => navigate('/tasks', { state: { openModal: true } })}>New Task</button>
                        </div>
                        <div className="p-task-list">
                            {tasks.map((t, idx) => <TaskItem key={idx} {...t} />)}
                        </div>
                    </div>

                    {/* Low Stock Alerts */}
                    {data.low_stock_alerts?.length > 0 && (
                        <div className="p-card alert-card">
                            <div className="p-card-header">
                                <h3 className="text-red">Inventory Alerts</h3>
                                <FontAwesomeIcon icon={faBell} className="text-red" />
                            </div>
                            <div className="alert-list">
                                {data.low_stock_alerts.map((alert, idx) => (
                                    <div key={idx} className="alert-item">
                                        <div className="alert-info">
                                            <span className="alert-name">{alert.item}</span>
                                            <span className="alert-sku">{alert.sku}</span>
                                        </div>
                                        <span className="alert-qty">{alert.stock} left</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Upcoming Deliveries (Trading) */}
                    {data.upcoming_deliveries?.length > 0 && (
                        <div className="p-card">
                            <div className="p-card-header">
                                <h3>Logistics Tracking</h3>
                                <button onClick={() => navigate('/orders')}>View Orders</button>
                            </div>
                            <div className="p-delivery-list">
                                {data.upcoming_deliveries.map((delivery, idx) => (
                                    <div key={idx} className="p-delivery-item">
                                        <div className="p-delivery-icon">
                                            <FontAwesomeIcon icon={faTruck} />
                                        </div>
                                        <div className="p-delivery-info">
                                            <div className="p-delivery-main">
                                                <strong>{delivery.order_number}</strong>
                                                <span className={`p-mini-badge ${delivery.status}`}>{delivery.status}</span>
                                            </div>
                                            <div className="p-delivery-sub">{delivery.customer} &middot; {delivery.address}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Admin Tools for Superadmin */}
                    {data.role === 'superadmin' && <AdminManagement data={data} refresh={fetchDashboard} />}
                </div>

                <div className="p-grid-right">
                    {/* Personal Progress Tracker */}
                    <PersonalProgressCard metrics={data.personal} />

                    {/* Stock Overview (Trading) */}
                    {data.stock_overview?.length > 0 && (
                        <div className="p-card mini-card">
                            <div className="p-card-header">
                                <h3>Stock Leaderboard</h3>
                                <FontAwesomeIcon icon={faBox} style={{color: '#64748b'}} />
                            </div>
                            <div className="p-stock-table-mini">
                                {data.stock_overview.map((s, idx) => (
                                    <div key={idx} className="p-stock-row">
                                        <div className="p-stock-name">
                                            <span>{s.name}</span>
                                            <small>{s.sku}</small>
                                        </div>
                                        <div className={`p-stock-qty ${s.stock < 10 ? 'low' : ''}`}>
                                            {s.stock}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <ProfileCard user={user_profile} stats={stats} onViewProfile={() => setShowProfileModal(true)} />
                    {showProfileModal && <ProfileDetailModal user={user_profile} onClose={() => setShowProfileModal(false)} />}

                    {/* Lead Status Distribution */}
                    {data.leads_distribution?.length > 0 && (
                        <div className="p-card mini-card">
                            <div className="p-card-header">
                                <h3>Lead Status</h3>
                                <FontAwesomeIcon icon={faChartLine} style={{color: '#64748b'}} />
                            </div>
                            <div style={{ height: '220px', width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={data.leads_distribution}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {data.leads_distribution.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={['#4f46e5', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'][index % 5]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" height={36}/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                    
                    <div className="p-card mini-card">
                        <h3>Workspace Intelligence</h3>
                        <p>This workspace is optimized for <strong>{data.workspace_type}</strong> workflows.</p>
                        <div className="p-intel-progress">
                            <div className="p-bar" style={{ width: '85%', background: 'linear-gradient(90deg, #3b9edd, #799F0C)' }}></div>
                        </div>
                        <span>Neural System Sync: 100% Active</span>
                        <div style={{marginTop: '1rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)'}}>
                            Precision tracking enabled for all active modules.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}